import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ManagerResult } from 'src/app/shared/models/search-result';
import { TranslationRequest } from 'src/app/shared/models/translation/translation-request';
import { TranslationPipe } from '../../pipes/translation.pipe';
import { Session } from '../../services/session.service';
import { UserRoleService } from '../../services/user-role.service';

export interface SummaryDashboardFilterValue {
  supervisor: ManagerResult | null;
  includeIndirect: boolean;
}

@Component({
  selector: 'app-filter-by-manager',
  providers: [TranslationPipe],
  template: `
    <form
      [formGroup]="formGroup"
      class="tw-w-full tw-flex tw-flex-wrap tw-items-center tw-px-2 tw-pt-2"
    >
    @if (clientHasSupervisorAccess) {
      <div class="tw-w-full md:tw-w-[30%]">
        <app-manager-selector
          formControlName="supervisor"
          [formGroup]="formGroup"
          [label]="filterBy + ' ' + Manager"
          ngDefaultControl
          [managerLookupSupervisor]="true"
          [managerLookupOmni]="false"
          (supervisorSelected)="onSupervisorSelected($event)"
          [sectionName]="('Manage delegate' | translation: translationRequest)"
        >
        </app-manager-selector>
      </div>
      <div
        class="tw-font-bold tw-w-full md:tw-w-[70%] tw-flex tw-justify-end"
      >
        <app-toggle-control
          formControlName="includeIndirect"
          [formGroup]="formGroup"
          label="Include indirect reports"
          labelClass="tw-font-bold app-color-slate"
          ngDefaultControl
        >
        </app-toggle-control>
      </div>
    }
    </form>
  `,
  styles: [``],
  standalone: false,
})
export class FilterByManagerComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() translationRequest: TranslationRequest;
  @Input() initialSupervisor: ManagerResult | null = null;
  @Input() initialIncludeIndirect: boolean = false;

  @Output() supervisorSelected = new EventEmitter<ManagerResult | null>();
  @Output() filterChange = new EventEmitter<SummaryDashboardFilterValue>();

  filterBy: string = 'Filter by';
  Manager: string = 'Manager';
  clientHasSupervisorAccess: boolean = false;

  formGroup = this.fb.group({
    supervisor: [null as ManagerResult | null],
    includeIndirect: [false],
  });

  private valueChangesSub: Subscription;

  constructor(
    private fb: FormBuilder,
    private translationPipe: TranslationPipe,
    private session: Session,
    public userRoleService: UserRoleService
  ) {}

  ngOnInit(): void {
    this.patchInitialValue(false);
    this.clientHasSupervisorAccess = !this.userRoleService.controller && !this.userRoleService.auditor ? true : false;
    this.valueChangesSub = this.formGroup.valueChanges.subscribe(
      (value: Partial<SummaryDashboardFilterValue>) => {
        this.filterChange.emit({
          supervisor: value.supervisor ?? null,
          includeIndirect: value.includeIndirect ?? false,
        });
      }
    );
    this.setManagerLabel();
    this.translationPipe.transform(this.filterBy, this.translationRequest);
    this.translationPipe.transform(this.Manager, this.translationRequest);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['initialSupervisor'] || changes['initialIncludeIndirect']) &&
      !changes['initialSupervisor']?.firstChange &&
      !changes['initialIncludeIndirect']?.firstChange
    ) {
      this.patchInitialValue(false);
    }
  }

  ngOnDestroy(): void {
    this.valueChangesSub?.unsubscribe();
  }

  onSupervisorSelected(supervisor: ManagerResult | null): void {
    this.supervisorSelected.emit(supervisor);
  }

  get value(): SummaryDashboardFilterValue {
    const v = this.formGroup.getRawValue();
    return {
      supervisor: v.supervisor ?? null,
      includeIndirect: v.includeIndirect ?? false,
    };
  }

  private patchInitialValue(emitEvent: boolean): void {
    this.formGroup.patchValue(
      {
        supervisor: this.initialSupervisor ?? null,
        includeIndirect: this.initialIncludeIndirect ?? false,
      },
      { emitEvent }
    );
  }

  private setManagerLabel(): void {
    if (
      this.translationRequest?.language === 'en' ||
      this.translationRequest?.language === 'es'
    ) {
      this.Manager = 'manager';
    } else {
      this.Manager = 'Manager';
    }
  }
}
